import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

const STATUS_VARIANT = { REQUESTED: 'warning', APPROVED: 'info', RECEIVED: 'success', REJECTED: 'danger' };

export function ReturnDetailModal({ returnId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const detail = useQuery({
    queryKey: ['sales', 'return', returnId],
    queryFn: () => salesApi.getReturn(returnId).then((r) => r.items),
    enabled: Boolean(returnId),
  });
  const ret = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales', 'returns'] });
    queryClient.invalidateQueries({ queryKey: ['sales', 'return', returnId] });
  };
  const run = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });

  const approve = useMutation(run(() => salesApi.approveReturn(returnId)));
  const reject = useMutation(run(() => salesApi.rejectReturn(returnId)));
  const receive = useMutation(run(() => salesApi.receiveReturn(returnId)));
  const busy = approve.isPending || reject.isPending || receive.isPending;

  return (
    <Modal
      open={Boolean(returnId)}
      onClose={onClose}
      title={ret ? `${t('ret.detail.title')} ${ret.returnNumber}` : t('ret.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {ret?.status === 'REQUESTED' && (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => reject.mutate()}>{t('ret.action.reject')}</Button>
              <Button variant="primary" disabled={busy} onClick={() => approve.mutate()}>{t('ret.action.approve')}</Button>
            </>
          )}
          {ret?.status === 'APPROVED' && (
            <Button variant="primary" disabled={busy} onClick={() => receive.mutate()}>{t('ret.action.receive')}</Button>
          )}
        </>
      }
    >
      {ret && (
        <>
          <p className="muted text-sm">
            <Badge variant={STATUS_VARIANT[ret.status]}>{t(`ret.status.${ret.status}`)}</Badge>
            {ret.reason ? ` · ${ret.reason}` : ''}
          </p>
          {ret.status === 'RECEIVED' && <div className="alert info">{t('ret.detail.received')}</div>}
          {ret.decisionNote && <div className="alert danger">{ret.decisionNote}</div>}
          {error && <div className="alert danger">{error}</div>}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('ret.field.product')}</th>
                  <th className="num">{t('ret.field.returnQty')}</th>
                  <th>{t('ret.field.lineReason')}</th>
                </tr>
              </thead>
              <tbody>
                {(ret.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td>{l.productName}</td>
                    <td className="num">{l.quantity}</td>
                    <td>{l.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
